import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import { Popup } from 'devextreme-react/popup';
import axios from "axios";
import SelectBox from 'devextreme-react/select-box';
import { GridLayout } from "../../../common/GridLayout";
import { formatQuantity } from "../../../common/Utils";
import { MESSAGES } from "../../../common/Messages";
import data from "../../../assets/apiRoutes.json";
import "./adjustAvailableUnavailable.css";
import notify from 'devextreme/ui/notify';
import { LoadPanel } from 'devextreme-react/load-panel';
import { confirm } from 'devextreme/ui/dialog';
import { Button } from 'devextreme-react/button';
import Toolbar, { Item as ToolbarItem } from "devextreme-react/toolbar";
import {
    Column,
    DataGrid,
    Scrolling,
    FilterRow,
    FilterPanel,
    Sorting,
    Paging,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
    // Aliased: this file already imports Toolbar/Item from devextreme-react/toolbar for the Popup.
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";

// Hoisted out of the component on purpose. Declared inside, these literals are rebuilt on every
// render, so devextreme-react sees a changed option each time and re-applies it to the widget — which
// made both grids reload (loading indicator on, off, on again) as the popup's state settled.
const allowedPageSizes = [10, 20, 100, 500, 1000, "all"];
const columnChooserSearchEditorOptions = { placeholder: 'Search column' };

const AdjustAvailableUnAvailableStatusPopup = ({ visible, onClose, selectedRows, accessToken }) => {
    // null = not fetched yet, [] = fetched and genuinely empty. The distinction drives noDataText
    // below so the grids do not flash "No data" before their first response arrives.
    const [reasonsCommentData, setReasonsCommentData] = useState(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [currentLayout, setCurrentLayout] = useState("Default");
    const [showIndicator, setShowIndicator] = useState(true);
    const [shading, setShading] = useState(true);
    const [showPane, setShowPane] = useState(true);

    // Distinct keys — the two grids previously shared one, so each overwrote the other's saved layout.
    const selectedRowsGridKey = "74AE551D-5992-4763-BD15-EDC6E0E45FF5";
    const reasonsGridKey = "1E0D8C42-9E2B-4F7A-9C58-2A6B0F3D71C4";
    // These must be built once per key, NOT called during render. A curried `loadState(key)` invoked
    // in JSX returns a brand-new function on every render, so customLoad/customSave would change
    // identity on each keystroke in the quantity fields — devextreme-react would then re-apply the
    // stateStoring option and DevExtreme would re-read the layout, one request per character typed.
    // The layout read is cached per key+layout. DevExtreme's state-storing controller asks for state
    // again whenever the grid's options are re-applied — and they are, because props like dataSource
    // and noDataText change once the first response lands — which fired GetGridLayout twice per grid.
    // The stored layout cannot change in between, so hand back the same promise; a failure clears the
    // entry so the next call retries instead of being stuck on a rejected promise.
    const layoutRequestsRef = useRef({});
    const stateStoringFor = useCallback((key) => ({
        customLoad: () => {
            const cache = layoutRequestsRef.current;
            if (!cache[key] || cache[key].layout !== currentLayout) {
                cache[key] = {
                    layout: currentLayout,
                    promise: GridLayout.sendStorageRequest(key, "GET", accessToken, null, currentLayout)
                        .catch((err) => { delete cache[key]; throw err; }),
                };
            }
            return cache[key].promise;
        },
        customSave: (state) => { GridLayout.sendStorageRequest(key, "POST", accessToken, state, currentLayout); },
    }), [currentLayout, accessToken]);
    const selectedRowsStateStoring = useMemo(() => stateStoringFor(selectedRowsGridKey), [stateStoringFor]);
    const reasonsStateStoring = useMemo(() => stateStoringFor(reasonsGridKey), [stateStoringFor]);

    const [hideOnOutsideClick, setHideOnOutsideClick] = useState(false);
    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, [setLoadPanelVisible]);
    // No `position`/`container` on the LoadPanel below: DevExtreme then centres it on the window,
    // which is what this dialog wants since it fills most of the viewport. Do not point it at an
    // element — '#accordion-container' is the search accordion on the host page (spinner lands above
    // the dialog), and scoping it to the popup's scrollable content div puts it beside the grid's own
    // built-in load indicator and clips the shading.
    const [availableFrom, setAvailableFrom] = useState(0);
    const [unAvailableFrom, setUnAvailableFrom] = useState(0);
    const [reasonsData, setReasonsData] = useState([]);

    const [selectedReason, setSelectedReason] = useState('');
    const [disableSingelRow, setDisableSingelRow] = useState(true);
    const [disableMultipleRows, setDisableMultipleRows] = useState(true);
    const [outAunavailableAfterCalculation, setoutAunavailableAfterCalculation] = useState(0);
    const [selectedSummaryData, setSummaryData] = useState(null);
    const summaryValueRef = useRef(null);

    // Delphi grdTopSelectedCountChange (uWMD0051.PAS:495-520): the footer totals are the sum across the
    // whole selection, shown alongside the single-row From/To panel.
    const totalSelectedAvailable = (selectedRows || []).reduce((sum, row) => sum + (Number(row?.available) || 0), 0);
    const totalSelectedUnavailable = (selectedRows || []).reduce((sum, row) => sum + (Number(row?.outUnavailable) || 0), 0);

    const getSummaryRecords = async () => {
        if (selectedRows.length>0) {
            const rowIds = selectedRows.map(item => item.rowid);
            const params = new URLSearchParams();
            rowIds.forEach(id => params.append("rowids", id));

            const res = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.LocationTracking.GetLocationQuantitySummaryByRowIds}`,
                {
                    params,
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );
            if (res.data) {
                // The OK path adjusts exactly one row, so anchor on the selected row's rowid rather than
                // res.data[0] — the by-rowids response is not guaranteed to preserve selection order.
                const anchorRowId = selectedRows.length === 1 ? selectedRows[0].rowid : null;
                summaryValueRef.current = anchorRowId
                    ? (res.data.find(r => r.rowid === anchorRowId) ?? res.data[0])
                    : res.data[0];
                setSummaryData(res.data);
            }
        }
    }

    const [comment, setComment] = useState(null);
    const onCommentFromChange = useCallback(
        (e) => {
            setComment(e.value);
        },
        [comment],
    );
    const [availableTo, setAvailableTo] = useState(null);
    // Delphi validates on every keystroke (edtAvailable_ToChange) but shows a MODAL dialog, so the
    // user cannot type another character until it is dismissed. A toast does not block, so typing an
    // invalid "500" raised three toasts that piled on top of each other. Keep the immediate feedback,
    // but only raise it when the message actually changes; clear the latch once the value is valid.
    const lastValidationMessageRef = useRef(null);
    const onAvailableToChange = useCallback(
        (e) => {
            const message = ValidateAvailableTo(e.value);
            if (message) {
                if (lastValidationMessageRef.current !== message) {
                    lastValidationMessageRef.current = message;
                    notify({ message }, 'error', 3000);
                }
            } else {
                lastValidationMessageRef.current = null;
            }
            setAvailableTo(e.value);
            CalculateAvailableTo(e.value);
        },
        [availableTo],
    );

    const [unAvailableTo, setUnAvailableTo] = useState(null);

    const handleChange = (value) => {
        setSelectedReason(value);
    };

    /** Reads the message off a MapServiceFailure response, falling back to the generic text. */
    const errorMessageFrom = (err) => err?.response?.data?.message ?? MESSAGES.FETCH_ERROR;

    const handleTransferResponse = (res) => {
        if (res.data && res.data.success) {
            notify({ message: res.data.message }, 'success', 2000);
            onClose();
        }
        else {
            notify({ message: res.data?.message ?? MESSAGES.ERROR }, 'error', 2000);
        }
        setTimeout(hideLoadPanel, 1000);
    };

    const handleTransferError = (err) => {
        notify({ message: errorMessageFrom(err) }, 'error', 4000);
        setTimeout(hideLoadPanel, 1000);
    };

    const postTransfer = (postUrl, requestData) => {
        setLoadPanelVisible(true);
        axios.post(`${window._env_.REACT_APP_APIS}/${postUrl}`, requestData,
            {
                headers: {
                    "Content-Type": "application/json",
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then(handleTransferResponse)
            .catch(handleTransferError);
    };

    /** Both bulk transfers move the whole quantity per row, matching Delphi FROMAVAILABLE/FROMUNAVAILABLE. */
    const buildBulkPayload = (quantityOf) => selectedRows.map(row => ({
        location: row.location,
        item: row.item,
        lot: row.lot,
        subLot: row.subLot,
        unit: row.unit,
        outUnavailable: quantityOf(row),
        rwarehouse: row.rwarehouse,
        comment: comment,
        reason: selectedReason,
    }));

    const handleClickTransferAvilableToUnavailableButton = () => {
        if (!selectedReason) {
            notify({ message: MESSAGES.ADJUST_REASON_REQUIRED }, 'error', 3000);
            return;
        }
        return confirm(MESSAGES.ADJUST_CONFIRM_AVAILABLE_TO_UNAVAILABLE, MESSAGES.WARNING).then(function (dialogResult) {
            if (dialogResult) {
                postTransfer(data.LocationTracking.TransferAvailableToUnAvailable,
                    buildBulkPayload(row => row.available));
            }
        });
    }

    const handleClickTransferUnavailableToAvilableButton = () => {
        if (!selectedReason) {
            notify({ message: MESSAGES.ADJUST_REASON_REQUIRED }, 'error', 3000);
            return;
        }
        return confirm(MESSAGES.ADJUST_CONFIRM_UNAVAILABLE_TO_AVAILABLE, MESSAGES.WARNING).then(function (dialogResult) {
            if (dialogResult) {
                postTransfer(data.LocationTracking.TransferUnAvailableToAvailable,
                    buildBulkPayload(row => row.outUnavailable));
            }
        });
    }

    const handleClickOkButton = () => {
        if (!selectedReason) {
            notify({ message: MESSAGES.ADJUST_REASON_REQUIRED }, 'error', 3000);
            return;
        }
        const message = ValidateAvailableTo(availableTo);
        if (message) {
            notify({ message }, 'error', 3000);
            return;
        }

        // Delphi btnSaveClick (uWMD0051.PAS:352) only inserts when Available_To differs from the
        // current AVAILABLE. Posting a zero delta would write a row that adjusts nothing. Delphi is
        // silent here; we say so, because a button that does nothing reads as broken on the web.
        if (!Number(outAunavailableAfterCalculation)) {
            notify({ message: MESSAGES.ADJUST_NOTHING_TO_ADJUST }, 'warning', 3000);
            return;
        }

        const source = summaryValueRef.current;
        postTransfer(data.LocationTracking.AdjustAvailableUnAvailable, {
            location: source.location || null,
            item: source.item || null,
            lot: source.lot || null,
            subLot: source.subLot || null,
            unit: source.unit || null,
            outUnavailable: outAunavailableAfterCalculation,
            rwarehouse: source.rwarehouse || null,
            comment: comment,
            reason: selectedReason || null,
        });
    };

    /**
     * Delphi edtAvailable_ToChange (uWMD0051.PAS:307): Available To must not exceed
     * Available From + Unavailable From. The lower bound is ours — the Delphi form accepted a negative.
     * Returns the message to show, or null when the value is valid.
     */
    const ValidateAvailableTo = (value) => {
        const requested = parseFloat(value);
        if (!Number.isFinite(requested)) {
            return null;
        }
        const available = parseFloat(summaryValueRef.current?.available ?? 0) || 0;
        const unavailable = parseFloat(summaryValueRef.current?.outUnavailable ?? 0) || 0;
        if (requested > available + unavailable) {
            return MESSAGES.ADJUST_AVAILABLE_EXCEEDS_TOTAL;
        }
        if (requested < 0) {
            return MESSAGES.ADJUST_AVAILABLE_NEGATIVE;
        }
        return null;
    }

    /**
     * Unavailable To is always derived, never typed (Delphi uWMD0051.PAS:319). The delta posted to the
     * API is the drop in Available. parseFloat, not parseInt — quantities carry two decimals.
     */
    const CalculateAvailableTo = (value) => {
        const requested = parseFloat(value) || 0;
        const available = parseFloat(summaryValueRef.current?.available ?? 0) || 0;
        const unavailable = parseFloat(summaryValueRef.current?.outUnavailable ?? 0) || 0;
        setUnAvailableTo((available + unavailable) - requested);
        setoutAunavailableAfterCalculation(available - requested);
    }

    const fetchReasonsData = async () => {
        setLoadPanelVisible(true);
        try {
            const urlgetreason = data.LocationTracking.GetReasonsCodes;
            var url = `${window._env_.REACT_APP_APIS}/${urlgetreason}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });
            // Delphi shows CODE + DESCRIPTION_1 in the reason lookup (CriteriaFields on edtReasonCode);
            // the stored value stays the code, which is what AVAILABLEREASONCODE holds.
            const formattedData = response.data.map(item => ({
                ID: item.rowid,
                Code: item.code,
                Text: item.description1 ? `${item.code} - ${item.description1}` : item.code,
            }));
            setReasonsData(formattedData);
            setTimeout(hideLoadPanel, 1000);
        } catch (error) {
            setTimeout(hideLoadPanel, 1000);
            console.error("Error fetching reasons data:", error);
        }
    };

    const fetchReasonsCommentsData = async () => {
        setLoadPanelVisible(true);
        // Delphi ShowUnavailableReasons filters on item + lot + sublot + location (unit deliberately
        // excluded), so send the whole key of the focused row rather than just item + lot.
        const anchor = selectedRows[0];
        let requestObj = {
            item: anchor.item,
            lot: anchor.lot,
            subLot: anchor.subLot,
            location: anchor.location,
        }
        try {
            const urlgetreason = data.LocationTracking.GetReasonComments;
            var url = `${window._env_.REACT_APP_APIS}/${urlgetreason}`;
            const response = await axios.post(url, requestObj, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });
            const formattedData2 = response.data.map(item => ({
                reasonCode: item.reasonCode,
                outUnavailable: item.outUnavailable,
                comment: item.comment,
            }));
            setReasonsCommentData(formattedData2);
            setTimeout(hideLoadPanel, 1000);
        } catch (error) {
            setTimeout(hideLoadPanel, 1000);
            console.error("Error fetching reasons data:", error);
        }
    };

    useEffect(() => {
        if (selectedRows.length == 1) {
            fetchReasonsCommentsData();
            fetchReasonsData();
            setDisableSingelRow(false);
            setDisableMultipleRows(true);
        } else if (selectedRows.length > 1) {
            fetchReasonsCommentsData();
            fetchReasonsData();
            setDisableSingelRow(true);
            setDisableMultipleRows(false);
        }
        getSummaryRecords();
    }, [selectedRows]);

    useEffect(() => {
        if (summaryValueRef.current) {
            setAvailableFrom(Number(summaryValueRef.current.available) || 0);
            setUnAvailableFrom(Number(summaryValueRef.current.outUnavailable) || 0);
        }
    }, [summaryValueRef.current])

    // Delphi keeps btnSave disabled until edtAvailable_ToChange accepts a value, and re-disables it on
    // every row scroll and whenever the bound check fails (uWMD0051.PAS:310/318/363). Ours was enabled
    // as soon as one row was selected, so OK could be pressed with the field untouched — and because
    // ValidateAvailableTo treats a non-numeric value as "nothing to complain about yet", that posted a
    // zero delta. Require a real number that passes validation.
    const availableToIsValid =
        Number.isFinite(parseFloat(availableTo)) && ValidateAvailableTo(availableTo) === null;

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={false}
            closeOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title="Status Update"
            container="#root"
            width="90vw"
            height="85vh"
        >
            <Toolbar>
            <ToolbarItem location="after">
                <Button className="gridTopButton" disabled={disableSingelRow || !availableToIsValid} onClick={handleClickOkButton} type="default" text="OK" />
            </ToolbarItem >
            <ToolbarItem location="after">
                <Button className="gridTopButton" onClick={(e) => onClose()} text="Cancel" />
            </ToolbarItem>
            <ToolbarItem location="after">
                <Button className="gridTopButton" disabled={disableMultipleRows} onClick={handleClickTransferAvilableToUnavailableButton} type="default" text="Available to Unavailable" />
            </ToolbarItem>
            <ToolbarItem location="after">
                <Button className="gridTopButton" disabled={disableMultipleRows} onClick={handleClickTransferUnavailableToAvilableButton} type="default" text="Unavailable to Available" />
            </ToolbarItem>
         </Toolbar>
            <div className="popup-content" style={{ overflow: 'auto', maxHeight: '100%' }}>
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    onHiding={hideLoadPanel}
                    visible={loadPanelVisible}
                    showIndicator={showIndicator}
                    shading={shading}
                    showPane={showPane}
                    hideOnOutsideClick={hideOnOutsideClick}
                />
                <div className="row">
                    <div className="col-md-3">
                        <NumberBox
                            disabled={disableSingelRow}
                            stylingMode='outlined'
                            labelMode="static"
                            format="#,##0.00"
                            value={availableFrom}
                            readOnly={true}
                            label="Quantity Available From"
                        />
                    </div>
                    <div className="col-md-3">
                        <NumberBox
                            disabled={disableSingelRow}
                            stylingMode='outlined'
                            labelMode="static"
                            style={{ width: '100%' }}
                            format="#,##0.00"
                            min={0}
                            value={availableTo}
                            onValueChanged={onAvailableToChange}
                            label="Quantity Available To"
                        />
                    </div>
                    <div className="col-md-3">
                        <NumberBox
                            disabled={disableSingelRow}
                            stylingMode='outlined'
                            labelMode="static"
                            style={{ width: '100%' }}
                            format="#,##0.00"
                            value={unAvailableFrom}
                            label="Quantity Unavailable From"
                            readOnly={true}
                        />
                    </div>
                    <div className="col-md-3">
                        <NumberBox
                            disabled={disableSingelRow}
                            stylingMode='outlined'
                            labelMode="static"
                            style={{ width: '100%' }}
                            format="#,##0.00"
                            value={unAvailableTo}
                            label="Quantity Unavailable To"
                            readOnly={true}
                        />
                    </div>
                </div>
                <div className="row accordianLastRow">
                    <div className="col-md-3">
                        <SelectBox
                            dataSource={reasonsData}
                            value={selectedReason}
                            valueExpr='Code'
                            displayExpr='Text'
                            searchEnabled={true}
                            stylingMode='outlined'
                            labelMode="static"
                            label="Reason"
                            onValueChange={handleChange}
                        />
                    </div>
                    <div className="col-md-3">
                        <TextBox
                            stylingMode='outlined'
                            labelMode="static"
                            style={{ width: '100%' }}
                            value={comment}
                            label="Comment"
                            onValueChanged={onCommentFromChange}
                        />
                    </div>
                </div>
                <div className="row accordianLastRow">
                    <div className="col-md-12">
                            <DataGrid

onExporting={(e) => {
          if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
          }
        }}
                                className="wm-popup-grid"
                                dataSource={selectedSummaryData}
                                noDataText={selectedSummaryData === null ? "" : "No data"}
                                allowColumnReordering={true}
                                allowColumnResizing={true}
                                showBorders={true}
                                hoverStateEnabled={true}
                                searchEnabled={true}
                                columnAutoWidth={true}
                            >
                                <ColumnChooser
                                    enabled={true}
                                    title="Choose Column"
                                    width={290}
                                    height={290}
                                    sortOrder="asc"
                                >
                                    <ColumnChooserSearch
                                        enabled={true}
                                        editorOptions={columnChooserSearchEditorOptions} />
                                </ColumnChooser>
                                <Export enabled={true} allowExportSelectedData={true} />
                                <Sorting visible={true} mode="multiple" />
                                <Paging defaultPageSize={10} />
                                <Pager
                                    visible={true}
                                    showPageSizeSelector={true}
                                    allowedPageSizes={allowedPageSizes}
                                    showNavigationButtons={true}
                                />
                                <FilterRow visible={true} />
                                <FilterPanel visible={true} />
                                <StateStoring
                                    enabled={true}
                                    type="custom"
                                    customLoad={selectedRowsStateStoring.customLoad}
                                    storageKey={selectedRowsGridKey}
                                    customSave={selectedRowsStateStoring.customSave}
                                />
                                <Scrolling mode="virtual" rowRenderingMode="virtual" />
                                <Column dataField="warehouse" caption="Warehouse" />
                                <Column dataField="locationCode" caption="Location" />
                                <Column dataField="itemCode" caption="Item" />
                                <Column dataField="lotCode" caption="Lot" />
                                <Column dataField="subLotCode" caption="Sublot" />
                                <Column dataField="unitCode" caption="Unit" />
                                <Column dataField="available" caption="Available" dataType="number" format="#,##0.00" alignment="right" />
                                <Column dataField="outUnavailable" caption="Unavailable" dataType="number" format="#,##0.00" alignment="right" />
                                <Column dataField="inTransactionNumber" caption="Lot First Receipt Transaction" />
                                <Column dataField="customer" caption="Customer" />
                                {/* Every row in this grid is a selected row, so these are the Delphi
                                    "Total Selected" captions for the set. In the grid header to match
                                    the main grid. Declaring a Toolbar replaces the default one, so the
                                    export and column-chooser buttons have to be re-declared. */}
                                {/* Plain `text` items, NOT JSX children. React children in a toolbar
                                    item make the DataGrid build a TemplateManager, which sets state
                                    after unmount when the popup closes. These totals are fixed for the
                                    dialog's lifetime, so they need no React component. */}
                                <GridToolbar>
                                    <GridToolbarItem
                                        location="after"
                                        cssClass="wm-toolbar-total-text"
                                        text={`Total Selected Available: ${formatQuantity(totalSelectedAvailable)}`}
                                    />
                                    <GridToolbarItem
                                        location="after"
                                        cssClass="wm-toolbar-total-text"
                                        text={`Total Selected Unavailable: ${formatQuantity(totalSelectedUnavailable)}`}
                                    />
                                    <GridToolbarItem name="exportButton" />
                                    <GridToolbarItem name="columnChooserButton" />
                                </GridToolbar>
                            </DataGrid>
                    </div>
                    {/* Always rendered, never conditional — Delphi keeps grdUnavailableReasons on
                        screen at all times and simply shows no rows when the record has no reason
                        history. Hiding it made the dialog's layout jump between records. */}
                    <div className="col-md-12">
                        <div className="wm-popup-grid-spacing">
                            <DataGrid

onExporting={(e) => {
          if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
          }
        }}
                                className="wm-popup-grid"
                                dataSource={reasonsCommentData}
                                noDataText={reasonsCommentData === null ? "" : "No data"}
                                allowColumnReordering={true}
                                allowColumnResizing={true}
                                showBorders={true}
                                hoverStateEnabled={true}
                                searchEnabled={true}
                                columnAutoWidth={true}
                            >
                                <ColumnChooser
                                    enabled={true}
                                    title="Choose Column"
                                    width={290}
                                    height={290}
                                    sortOrder="asc"
                                >
                                    <ColumnChooserSearch
                                        enabled={true}
                                        editorOptions={columnChooserSearchEditorOptions} />
                                </ColumnChooser>
                                <Export enabled={true} allowExportSelectedData={true} />
                                <Sorting visible={true} mode="multiple" />
                                <Paging defaultPageSize={10} />
                                <Pager
                                    visible={true}
                                    showPageSizeSelector={true}
                                    allowedPageSizes={allowedPageSizes}
                                    showNavigationButtons={true}
                                />
                                <FilterRow visible={true} />
                                <FilterPanel visible={true} />
                                <StateStoring
                                    enabled={true}
                                    type="custom"
                                    customLoad={reasonsStateStoring.customLoad}
                                    storageKey={reasonsGridKey}
                                    customSave={reasonsStateStoring.customSave}
                                />
                                <Scrolling mode="virtual" rowRenderingMode="virtual" />
                                <Column dataField="reasonCode" caption="Reason" />
                                <Column dataField="outUnavailable" caption="Unavailable" dataType="number" format="#,##0.00" alignment="right" />
                                <Column dataField="comment" caption="Comment" />
                            </DataGrid>
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    )
};

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(AdjustAvailableUnAvailableStatusPopup);
